import React, { useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { FileEvent } from '@components/buttons/BrowseFilesButton';
import UploadFilePanel from '@components/panels/UploadFile/UploadFilePanel';
import { UserFormStates, useUserJourneyContext } from '@context/UserJourneyContext';
import { logger as sentryLogger } from '@sentry/react';
import Button from '@components/buttons/Button/Button';
import Elliptical from '@components/spinners/Elliptical/Elliptical';
import WarningModal from '@components/modal/info/Warning';
import { postWithIdToken } from "@utils/http/MSAuthAPI";


/**
 * Document upload component specifically for the Uber HP Car Only Promo journey.
 * This component allows users to upload required documents before proceeding with the onboarding.
 */
export default function HPCarOnlyDocumentUpload() {
    const ALLOWED_FILE_TYPES = '.pdf, .png, .jpeg, .jpg';

    const { formState, updateFormState } = useUserJourneyContext();
    const { contractDetails, driverDetails } = formState;

    const msalContext = useMsal();
    const isAuthenticated = useIsAuthenticated();

    const [insuranceDocument, setInsuranceDocument] = useState<File>();
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    const onSelectInsuranceDocument = (event: FileEvent) => {
        setInsuranceDocument(event.file);
        setUploadSuccess(false); // Reset success state when new file is selected
    };

    // Check if all required documents are uploaded
    const areAllRequiredDocumentsUploaded = (): boolean => {
        return !!(insuranceDocument);
    };

    // Generate timestamp for filename
    const generateTimestamp = (): string => {
        const now = new Date();
        return now.toISOString().replace(/[:.]/g, '-').slice(0, -5); // Format: YYYY-MM-DDTHH-MM-SS
    };

    const handleUploadInsurance = async () => {
        if (!insuranceDocument) {
            return;
        }

        setIsUploading(true);
        setUploadError(false);

        try {
            const timestamp = generateTimestamp();
            const fileExtension = insuranceDocument.name.split('.').pop();
            const fileName = `insurance_document_${timestamp}.${fileExtension}`;

            sentryLogger.info(`Preparing insurance document upload: ${fileName}`);

            // Prepare document upload request for document-store endpoint
            const requestBody = {
                document_type: 'Insurance',
                description: 'Insurance Document',
                files_original_name: [fileName],
                files_extension: [`.${fileExtension}`],
                files_description: ['Insurance Document']
            };

            sentryLogger.info(`Uploading insurance document for contact: ${driverDetails.crmId}`);

            // Call document-store upload endpoint
            const response = await postWithIdToken(
                `/v1/document-store/customer/${driverDetails.country}/${driverDetails.crmId}`,
                { msalContext, isAuthenticated },
                {},
                requestBody
            );

            if (response.status >= 400) {
                throw new Error(`Upload preparation failed with status: ${response.status}`);
            }

            const responseData = response.data;
            sentryLogger.info(`Insurance document upload prepared with document_store_record_id: ${responseData.document_store_record_id}`);

            // Store document_store_record_id in context for later use in onboarding
            updateFormState(UserFormStates.contractDetails, {
                insuranceDocStoreId: responseData.document_store_record_id,
                documentUploadComplete: false  // Not complete until S3 upload succeeds
            });

            // Upload the file to S3 using presigned URL
            if (responseData.presigned_upload_urls && responseData.presigned_upload_urls[0]?.presigned_upload_url) {
                const uploadResponse = await fetch(responseData.presigned_upload_urls[0].presigned_upload_url, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': insuranceDocument.type
                    },
                    body: insuranceDocument
                });

                if (uploadResponse.ok) {
                    sentryLogger.info(`Insurance document uploaded successfully to S3`);
                    setUploadSuccess(true);
                    // Mark upload as complete in context to allow form progression
                    updateFormState(UserFormStates.contractDetails, {
                        documentUploadComplete: true
                    });
                } else {
                    sentryLogger.error(`Insurance document S3 upload failed with status: ${uploadResponse.status}`);
                    setUploadError(true);
                    // Clear the stored ID and completion flag if upload failed
                    updateFormState(UserFormStates.contractDetails, {
                        insuranceDocStoreId: "",
                        documentUploadComplete: false
                    });
                }
            } else {
                throw new Error('Missing presigned upload URL for insurance document');
            }
        } catch (error) {
            sentryLogger.error(`Insurance document upload error: ${error}`);
            console.error('Upload error:', error);
            setUploadError(true);
            // Clear the stored ID and completion flag if any error occurred
            updateFormState(UserFormStates.contractDetails, {
                insuranceDocStoreId: "",
                documentUploadComplete: false
            });
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div style={{ margin: '2.5vh 2.5vw' }}>
            <p style={{ marginTop: '2vh', marginBottom: '3vh' }}>
                Please upload the following documents for the Uber HP Car Only Promo journey.
                All documents are required to proceed.
            </p>

            {isUploading && (
                <div
                    data-testid="loading-spinner"
                    style={{ display: 'flex', justifyContent: 'center', margin: '15vh' }}
                >
                    <Elliptical />
                </div>
            )}

            {!isUploading && (
                <>
                    <div style={{ marginBottom: '3vh' }}>
                        <p><b>Insurance Document</b></p>
                        <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '1vh' }}>
                            Please upload proof of insurance for this vehicle
                        </p>
                        {!uploadSuccess && (
                            <>
                                <UploadFilePanel
                                    allowedTypes={ALLOWED_FILE_TYPES}
                                    onSelect={onSelectInsuranceDocument}
                                    required={true}
                                />
                                {insuranceDocument && (
                                    <div style={{ marginTop: '1vh' }}>
                                        <p style={{ fontSize: '0.9em', color: '#666' }}>
                                            Selected: {insuranceDocument.name}
                                        </p>
                                        <Button
                                            label="Upload Insurance Document"
                                            onClickFunc={handleUploadInsurance}
                                            disabled={isUploading}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {uploadSuccess && (
                        <div style={{
                            marginTop: '2vh',
                            marginBottom: '2vh',
                            padding: '1.5vh',
                            backgroundColor: '#d4edda',
                            border: '1px solid #c3e6cb',
                            borderRadius: '5px'
                        }}>
                            <p style={{ margin: 0, color: '#155724' }}>
                                ✓ Insurance document uploaded successfully to Zoho contact!
                            </p>
                        </div>
                    )}

                    <div style={{
                        marginTop: '3vh',
                        padding: '1.5vh',
                        backgroundColor: uploadSuccess ? '#d4edda' : '#fff3cd',
                        border: `1px solid ${uploadSuccess ? '#c3e6cb' : '#ffeaa7'}`,
                        borderRadius: '5px'
                    }}>
                        <p style={{ margin: 0 }}>
                            {uploadSuccess
                                ? '✓ All required documents have been uploaded. You may proceed to the next step.'
                                : 'Please upload all required documents to continue.'}
                        </p>
                    </div>
                </>
            )}

            {uploadError && (
                <WarningModal
                    message="Failed to upload insurance document. Please try again."
                    additionalInfo=""
                    onClickClose={() => setUploadError(false)}
                />
            )}
        </div>
    );
}
